import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';

const AddUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoader, setIsLoader] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_unlimited_sessions: true,
    no_sessions: '',
    duration: '',
    duration_type: 'month',
    price: '',
    status: true,
  });

  useEffect(() => {
    if (id) getSingleData();
  }, [id]);

  const getSingleData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/membership-plans/${id}`,
      });
      const data = response.data;
      if (data.status) {
        const plan = data.data;
        setFormData({
          title: plan.title || '',
          description: plan.description || '',
          is_unlimited_sessions: !!plan.is_unlimited_sessions,
          no_sessions: plan.no_sessions ?? '',
          duration: plan.duration ?? '',
          duration_type: plan.duration_type ?? 'month',
          price: plan.price ?? '',
          status: !!plan.status,
        });
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoader(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.is_unlimited_sessions && !formData.no_sessions) {
      toast.error('Set the number of sessions, or make the plan unlimited');
      return;
    }

    setIsLoader(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('is_unlimited_sessions', formData.is_unlimited_sessions);
      data.append('no_sessions', formData.is_unlimited_sessions ? '' : formData.no_sessions);
      data.append('duration', formData.duration);
      data.append('duration_type', formData.duration_type);
      data.append('price', formData.price);
      data.append('status', formData.status);

      const response = await ApiService.request({
        method: 'POST',
        url: `admin/membership-plans/createOrUpdate/${id || ''}`,
        data,
      });

      if (response.data.status) {
        toast.success(response.data.message);
        navigate('/admin/membership-plans');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {id ? 'Edit Membership Plan' : 'Add Membership Plan'}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/membership-plans" className="text-muted text-hover-primary">Membership Plans</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body py-6">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-6">
                    <label className="required form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="e.g. Unlimited Monthly"
                    />
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Duration</label>
                    <input
                      type="number"
                      min="1"
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="e.g. 1"
                    />
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Duration Type</label>
                    <select
                      name="duration_type"
                      value={formData.duration_type}
                      onChange={handleChange}
                      className="form-select form-control-solid"
                    >
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </select>
                  </div>

                  <div className="col-md-12 mb-6">
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      rows="3"
                      placeholder="What the plan includes"
                    />
                  </div>

                  <div className="col-md-6 mb-6">
                    <div className="form-check form-switch form-check-custom form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="is_unlimited_sessions"
                        checked={formData.is_unlimited_sessions}
                        onChange={handleChange}
                        id="is_unlimited_sessions"
                      />
                      <label className="form-check-label" htmlFor="is_unlimited_sessions">
                        Unlimited sessions
                      </label>
                    </div>
                  </div>

                  {!formData.is_unlimited_sessions && (
                    <div className="col-md-6 mb-6">
                      <label className="required form-label">Number of Sessions</label>
                      <input
                        type="number"
                        min="1"
                        name="no_sessions"
                        value={formData.no_sessions}
                        onChange={handleChange}
                        className="form-control form-control-solid"
                        placeholder="e.g. 10"
                      />
                    </div>
                  )}

                  <div className="col-md-6 mb-6">
                    <div className="form-check form-switch form-check-custom form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="status"
                        checked={formData.status}
                        onChange={handleChange}
                        id="status"
                      />
                      <label className="form-check-label" htmlFor="status">
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3">
                  <Link to="/admin/membership-plans" className="btn btn-light">Cancel</Link>
                  <button type="submit" className="btn btn-base" disabled={isLoader}>
                    {isLoader ? <Loader text="Saving" size="20" /> : (id ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUpdate;
